/**
 * Onglet Vente — composition du panier + paiement.
 *
 * v1 (Mois 1) : UI minimale mais fonctionnelle.
 * - Choisit des articles depuis le catalogue local (Dexie)
 * - Compose le panier (quantité + prix)
 * - Valide → crée la vente locale + l'empile dans l'outbox sync
 *
 * Le calcul (total, monnaie, détection crédit) est en MOCK LOCAL — quand
 * le Worker Core est branché, on l'appelle en ligne, sinon on retombe sur
 * la version locale.
 */
import { useEffect, useState } from 'react';
import { fcfa, formatFcfa } from '@boutikos/domain';
import { db, type ArticleRow } from '../lib/db';
import { creerVenteLocale, type LigneVente, type PartPaiement, syncPull } from '../lib/api';
import { argent, heureCourte } from '../lib/format';

export function Vente() {
  const [articles, setArticles] = useState<ArticleRow[]>([]);
  const [panier, setPanier] = useState<LigneVente[]>([]);
  const [paiement, setPaiement] = useState<PartPaiement[]>([{ mode: 'cash', montant: 0 }]);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const rows = await db.articles.where('_sync').equals('synced').toArray();
      // Démo : si vide, on injecte un mini-catalogue de test (à enlever quand import masse)
      if (rows.length === 0) {
        const seed: ArticleRow[] = [
          { id: 'demo-riz', nom: 'Riz 5kg', prix_vente: 6500, stock_actuel: 50, seuil_rupture: 5, en_vitrine: 1, updated_at: Date.now(), _sync: 'synced' },
          { id: 'demo-huile', nom: 'Huile 1L', prix_vente: 1200, stock_actuel: 80, seuil_rupture: 10, en_vitrine: 1, updated_at: Date.now(), _sync: 'synced' },
          { id: 'demo-savon', nom: 'Savon', prix_vente: 250, stock_actuel: 200, seuil_rupture: 20, en_vitrine: 1, updated_at: Date.now(), _sync: 'synced' },
        ];
        await db.articles.bulkPut(seed);
        setArticles(seed);
      } else {
        setArticles(rows);
      }
    })();
  }, []);

  const ajouterAuPanier = (art: ArticleRow) => {
    setPanier((p) => {
      const ex = p.find((l) => l.articleId === art.id);
      if (ex) {
        return p.map((l) => (l.articleId === art.id ? { ...l, qte: l.qte + 1 } : l));
      }
      return [
        ...p,
        {
          articleId: art.id,
          nom: art.nom,
          prixUnitaire: art.prix_vente,
          qte: 1,
        },
      ];
    });
  };

  const retirerLigne = (articleId: string) => {
    setPanier((p) => p.filter((l) => l.articleId !== articleId));
  };

  const modifierQte = (articleId: string, qte: number) => {
    if (qte < 1) return retirerLigne(articleId);
    setPanier((p) => p.map((l) => (l.articleId === articleId ? { ...l, qte } : l)));
  };

  const total = panier.reduce(
    (s, l) => s + fcfa(fcfa(l.prixUnitaire) * l.qte),
    0,
  );
  constrecu = paiement.reduce((s, p) => s + p.montant, 0);
  const monnaie = Math.max(0, recu - total);

  const valider = async () => {
    if (panier.length === 0) {
      setMessage('Panier vide');
      return;
    }
    if (recu < total) {
      setMessage(`Paiement insuffisant (manque ${argent(total - recu)})`);
      return;
    }
    try {
      await creerVenteLocale({
        userId: 'demo-user',
        lignes: panier,
        paiement,
      });
      // Tente une sync immédiate (best-effort)
      try { await syncPull(); } catch { /* offline OK, on retentera */ }
      setPanier([]);
      setPaiement([{ mode: 'cash', montant: 0 }]);
      setMessage(`✅ Vente encaissée ${new Date().toLocaleTimeString('fr-BJ')}`);
    } catch (e) {
      setMessage(`Erreur : ${(e as Error).message}`);
    }
  };

  return (
    <>
      <h1 className="titre-section">🛒 Vente</h1>

      {message && (
        <div className="carte" role="status" style={{ background: '#ecfdf5', borderColor: '#a7f3d0' }}>
          {message}
        </div>
      )}

      <section className="carte">
        <h3>Catalogue</h3>
        {articles.length === 0 ? (
          <p className="etat-vide">Aucun article</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
            {articles.map((a) => (
              <button
                key={a.id}
                className="bouton bouton-secondaire"
                style={{ flexDirection: 'column', padding: 12, height: 'auto' }}
                onClick={() => ajouterAuPanier(a)}
              >
                <strong style={{ fontSize: 13 }}>{a.nom}</strong>
                <span style={{ fontSize: 12, color: 'var(--couleur-texte-doux)' }}>{argent(a.prix_vente)}</span>
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="carte">
        <h3>Panier ({panier.length})</h3>
        {panier.length === 0 ? (
          <p className="etat-vide">Touche un article pour l'ajouter</p>
        ) : (
          panier.map((l) => (
            <div key={l.articleId} className="ligne">
              <div>
                <div>{l.nom}</div>
                <div style={{ fontSize: 12, color: 'var(--couleur-texte-doux)' }}>
                  {argent(l.prixUnitaire)} × {l.qte} = {argent(fcfa(fcfa(l.prixUnitaire) * l.qte))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button className="bouton bouton-secondaire" onClick={() => modifierQte(l.articleId, l.qte - 1)}>−</button>
                <button className="bouton bouton-secondaire" onClick={() => modifierQte(l.articleId, l.qte + 1)}>+</button>
                <button className="bouton bouton-danger" onClick={() => retirerLigne(l.articleId)}>✕</button>
              </div>
            </div>
          ))
        )}
      </section>

      <section className="carte">
        <h3>Paiement</h3>
        {paiement.map((p, i) => (
          <div key={i} className="ligne" style={{ alignItems: 'flex-end' }}>
            <label style={{ flex: 1 }}>
              <div style={{ fontSize: 12, marginBottom: 4 }}>Mode</div>
              <select
                value={p.mode}
                onChange={(e) => {
                  const v = e.target.value as PartPaiement['mode'];
                  setPaiement((pp) => pp.map((x, j) => (j === i ? { ...x, mode: v } : x)));
                }}
                style={{ padding: 8, borderRadius: 6, border: '1px solid var(--couleur-bordure)' }}
              >
                <option value="cash">Cash</option>
                <option value="momo">MoMo</option>
                <option value="orange">Orange Money</option>
                <option value="moov">Moov Money</option>
                <option value="credit">Crédit</option>
              </select>
            </label>
            <label style={{ flex: 1 }}>
              <div style={{ fontSize: 12, marginBottom: 4 }}>Montant (F)</div>
              <input
                type="number"
                inputMode="numeric"
                value={p.montant || ''}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10) || 0;
                  setPaiement((pp) => pp.map((x, j) => (j === i ? { ...x, montant: v } : x)));
                }}
                style={{ padding: 8, borderRadius: 6, border: '1px solid var(--couleur-bordure)', width: '100%' }}
              />
            </label>
          </div>
        ))}
        <div className="ligne" style={{ fontSize: 18, fontWeight: 700, color: 'var(--couleur-primaire)' }}>
          <span>Total</span>
          <span>{argent(total)}</span>
        </div>
        <div className="ligne">
          <span>Reçu</span>
          <span>{argent(recu)}</span>
        </div>
        {monnaie > 0 && (
          <div className="ligne" style={{ color: 'var(--couleur-accent)', fontWeight: 700 }}>
            <span>Monnaie à rendre</span>
            <span>{argent(monnaie)}</span>
          </div>
        )}
        <button
          className="bouton bouton-primaire bouton-pleine-largeur"
          style={{ marginTop: 12 }}
          onClick={valider}
          disabled={panier.length === 0}
        >
          💰 Encaisser
        </button>
      </section>
    </>
  );
}
